import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button as ChakraButton,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { exchangeBook } from '@partiu/api/bookstore/api';
import { useAPICallAsMutation } from '@partiu/api/hooks';
import { IBook } from '@partiu/api/types';
import React, { useRef } from 'react';
import { LuMoveRight } from 'react-icons/lu';
import { useQueryClient } from 'react-query';
import { Button } from '../../../../../../packages/ui/components/atoms/Button';
import { SectionTitle } from '../../../../../../packages/ui/components/atoms/Title';
import { useTheme } from '../../../../../../packages/ui/contexts/ThemeProvider';
import { useNavigate } from 'react-router-dom';

const ExchangeableBook: React.FC<{ book: IBook; bookUserId: string }> = ({ book, bookUserId }) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { onOpen: onOpenModal, onClose: onCloseModal, isOpen: isOpenModal } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { id } = useTheme();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const exchangeBookMutation = useAPICallAsMutation(exchangeBook, {
    onSuccess() {
      queryClient.invalidateQueries();
      toast({
        title: 'Troca realizada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      navigate(-1);
    },
    onError() {
      toast({
        title: 'Erro ao realizar a troca',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  return (
    <VStack
      borderRadius={'10px'}
      align={'flex-start'}
      backgroundColor={'white'}
      width={'100%'}
      border="1px solid #ededed"
      padding="8px"
      boxShadow={'rgba(100, 100, 111, 0.2) 0px 5px 8px 0px'}
      transition="transform 0.5s ease-in-out"
      _hover={{ transform: 'scale(1.15)' }}
    >
      <VStack
        position={'relative'}
        spacing={0}
        cursor={'pointer'}
        onClick={onOpenModal}
        align={'flex-start'}
        width={'100%'}
      >
        <Image
          boxShadow={'rgba(100, 100, 111, 0.2) 0px 5px 8px 0px'}
          borderRadius={'5px'}
          aspectRatio={0.7}
          border={'1px solid #ededed'}
          width={'100%'}
          objectFit={'cover'}
          src={book.coverOptimized || book.cover}
        />
        <Heading
          paddingTop="8px"
          width={'100%'}
          whiteSpace={'nowrap'}
          overflowX={'clip'}
          textOverflow={'ellipsis'}
          fontSize={'12px'}
        >
          {book.title}
        </Heading>
        <Text color="#787878" fontSize={'12px'}>
          {book.author}
        </Text>
      </VStack>
      <AlertDialog leastDestructiveRef={cancelRef} isOpen={isOpen} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent maxWidth="600px" marginX="8px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Tem certeza que deseja trocar por esse livro?
            </AlertDialogHeader>
            <AlertDialogFooter>
              <ChakraButton variant={'ghost'} ref={cancelRef} onClick={onClose}>
                Cancelar
              </ChakraButton>
              <ChakraButton
                colorScheme="green"
                onClick={() =>
                  book &&
                  id &&
                  exchangeBookMutation.mutate({ currentBookUserId: bookUserId, newBookId: String(book.id) })
                }
                ml={3}
                isLoading={exchangeBookMutation.isLoading}
              >
                Confirmar
              </ChakraButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Modal isCentered size={'sm'} isOpen={isOpenModal} onClose={onCloseModal}>
        <ModalOverlay backdropFilter={'blur(10px)'} />
        <ModalContent borderRadius={'20px'}>
          <ModalBody>
            <VStack spacing={5} paddingTop={'30px'} paddingBottom={'10px'}>
              <HStack position={'relative'} w={'100%'} align={'center'} justify={'center'}>
                <Image alignSelf={'center'} w={'50%'} aspectRatio={0.7} objectFit={'cover'} src={book?.cover} />
              </HStack>

              <VStack spacing={0}>
                <SectionTitle color="black" textAlign={'center'}>
                  {book?.title}
                </SectionTitle>
                {book?.author && (
                  <Text fontSize={'14px'} color={'#a9a9a9'}>
                    {book?.author}
                  </Text>
                )}
              </VStack>
              <div
                style={{ maxHeight: '250px', overflowY: 'scroll', borderBottom: '1px solid #dedede' }}
                dangerouslySetInnerHTML={{ __html: book?.description || '' }}
              ></div>
              <HStack w={'100%'}>
                <Button
                  onClick={onCloseModal}
                  maxW={'150px'}
                  isLoading={exchangeBookMutation.isLoading}
                  alignSelf={'flex-end'}
                  backgroundColor={'white'}
                  border="2px solid black"
                  color="black"
                  _hover={{ backgroundColor: 'whiteAlpha.600' }}
                >
                  Fechar
                </Button>
                <Button
                  onClick={onOpen}
                  maxW={'200px'}
                  isLoading={exchangeBookMutation.isLoading}
                  alignSelf={'flex-end'}
                  rightIcon={<LuMoveRight fontSize={'24px'} />}
                >
                  Trocar
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ExchangeableBook;
